import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Download } from "lucide-react";
import { useRef } from "react";

const CATALOGUE_PDF_URL = "https://fokallamps.com/catalog.pdf";

const DIVISIONS_DATA = [
  {
    title: "Signal, Stop & Indicator Lamps",
    products: "P21W · PY21W · P21/5W · R10W · R5W",
    subtext: "Turn, stop, tail & indicator applications",
    image: "products/P21.png",
  },
  {
    title: "Wedge & Miniature Lamps",
    products: "T10 / W5W · T13 · Instrument cluster lamps",
    subtext: "Instrument cluster & auxiliary lighting",
    image: "products/T10.png",
  },
  {
    title: "Headlamp & Forward Lighting",
    products: "H4 · HS1 · H7",
    subtext: "Headlamp & forward illumination",
    note: "OEM-grade halogen headlamps for regulated markets",
    image: "products/HS1-H4.png",
  },
  {
    title: "Interior & Auxiliary Lamps",
    products: "Festoon · Dome · Cabin lamps",
    subtext: "Interior, dome & courtesy lighting",
    image: "products/auxilary.png",
  },
  {
    title: "Commercial Vehicle & 24V Lamps",
    products: "24V variants · Heavy-duty lamps",
    subtext: "Truck, bus & off-highway applications",
    image: "products/24V.png",
  },
  {
    title: "Defence & Specialised Lamps",
    products: "Custom voltage lamps · Platform-specific solutions",
    subtext: "Mission-critical & regulated applications",
  },
];

export const DivisionsSection = ({ isDesktop, THEME }) => {
  const scrollRef = useRef(null);

  const scrollByAmount = (direction) => {
    if (!scrollRef.current) return;
    const cardWidth = isDesktop ? 420 : window.innerWidth * 0.9;
    scrollRef.current.scrollBy({
      left: direction * cardWidth,
      behavior: "smooth",
    });
  };

  return (
    <section
      id="products"
      style={{
        width: "100%",
        position: "relative",
        backgroundColor: "transparent",
      }}
    >
      {/* RED HALF BACKGROUND */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "420px",
          backgroundColor: "#cc0000",
          zIndex: 0,
        }}
      />

      {/* CONTENT */}
      <div
        style={{
          transform: "scale(0.95)",
          position: "relative",
          zIndex: 10,
          paddingTop: "80px",
          maxWidth: "1600px",
          margin: "0 auto",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
            justifyContent: "space-between",
            alignItems: isDesktop ? "flex-end" : "flex-start",
            marginBottom: "64px",
            gap: "32px",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "2px",
                  backgroundColor: THEME.colors.accent,
                }}
              />
              <span
                style={{
                  color: THEME.colors.accent,
                  fontWeight: 800,
                  letterSpacing: "0.2em",
                  fontSize: "12px",
                  textTransform: "uppercase",
                }}
              >
                Product Categories
              </span>
            </div>

            <h2
              style={{
                color: "white",
                fontSize: isDesktop ? "42px" : "32px",
                fontWeight: 900,
                textTransform: "uppercase",
                lineHeight: 1.1,
                maxWidth: "700px",
                margin: 0,
              }}
            >
              Comprehensive Automotive & Defence Lighting Portfolio
            </h2>
          </div>

          {/* ARROWS */}
          <div style={{ display: "flex", gap: "16px" }}>
            <motion.button
              onClick={() => scrollByAmount(-1)}
              whileHover={{ backgroundColor: "white", color: "#cc0000" }}
              style={arrowStyle}
            >
              <ArrowLeft size={20} />
            </motion.button>
            <motion.button
              onClick={() => scrollByAmount(1)}
              whileHover={{ backgroundColor: "white", color: "#cc0000" }}
              style={arrowStyle}
            >
              <ArrowRight size={20} />
            </motion.button>
          </div>
        </div>

        {/* SCROLLING CARDS */}
        <div
          ref={scrollRef}
          className="hide-scrollbar"
          style={{
            display: "flex",
            gap: "32px",
            overflowX: "auto",
            overflowY: "clip",
            paddingBottom: "24px",
            scrollSnapType: "x mandatory",
          }}
        >
          {DIVISIONS_DATA.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.08 }}
              style={{
                flex: "0 0 auto",
                width: isDesktop ? "380px" : "90vw",
                backgroundColor: "white",
                padding: "32px",
                border: "2px solid #e4e4e7",
                scrollSnapAlign: "start",
              }}
            >
              {/* IMAGE CONTAINER */}
              <div
                style={{
                  height: "200px",
                  marginBottom: "24px",
                  backgroundColor: "#f4f4f5",
                  border: "2px solid #e4e4e7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <img
                  src={item.image} // 👈 add image path in data later
                  alt={item.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain", // safest for product images
                    padding: "16px",
                    transition: "transform 0.4s ease",
                  }}
                />

                {/* Optional fallback text (shows only if image missing) */}
                {!item.image && (
                  <span
                    style={{
                      position: "absolute",
                      color: "#71717a",
                      fontSize: "12px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    Product Image
                  </span>
                )}
              </div>

              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  marginBottom: "12px",
                }}
              >
                {item.title}
              </h3>

              <p
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#09090b",
                  marginBottom: "8px",
                }}
              >
                {item.products}
              </p>

              <p
                style={{
                  fontSize: "14px",
                  color: "#52525b",
                  lineHeight: 1.6,
                  marginBottom: item.note ? "12px" : "0",
                }}
              >
                {item.subtext}
              </p>

              {item.note && (
                <p
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#09090b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {item.note}
                </p>
              )}
            </motion.div>
          ))}
        </div>

        {/* CATALOGUE CTA */}
        <motion.a
          href={CATALOGUE_PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "12px",
            padding: "16px 28px",
            background:
              "linear-gradient(135deg, #B8962E 0%, #FFD97A 50%, #8F6B1F 100%)",
            color: "white",
            fontWeight: 800,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            textDecoration: "none",
            marginTop: "40px",
          }}
        >
          <Download size={18} />
          Download Full Catalogue
        </motion.a>
      </div>
    </section>
  );
};

const arrowStyle = {
  width: "48px",
  height: "48px",
  border: "1px solid rgba(255,255,255,0.3)",
  backgroundColor: "transparent",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};
